import { Component, computed, inject, TemplateRef, viewChild } from "@angular/core";
import { PopoverDirective } from "@kno2/common/directives";
import { PluckPipe } from "@kno2/common/pipes";
import { NotificationService } from "@kno2/common/services/notifications";
import { reloadAfterRemoval, usePagedList } from "@kno2/core/http";
import { AdminLicenseTypesService, LicenseType, LicenseTypeQueryOptions } from "@kno2/data-access/license-types";
import { ConfirmationDialogService } from "@kno2/shared/confirmation-dialog";
import { DialogService } from "@kno2/shared/dialog";
import { MultiValueDisplayComponent } from "@kno2/shared/multi-value-display";
import { PaginationComponent, PaginationPageSizeComponent, PaginationSearchComponent } from "@kno2/shared/pagination";
import { TableColumn, TableComponent, TableSortEvent } from "@kno2/shared/table";
import { AdminLicenseTypesEditModalComponent } from "./admin-license-types-edit-modal.component";

@Component({
    selector: "kno2-admin-license-types",
    imports: [
        TableComponent,
        PaginationPageSizeComponent,
        PaginationSearchComponent,
        PaginationComponent,
        MultiValueDisplayComponent,
        PopoverDirective,
        PluckPipe
    ],
    templateUrl: "./admin-license-types.component.html"
})
export class AdminLicenseTypesComponent {
    private readonly dialogService = inject(DialogService);
    private readonly adminLicenseTypesService = inject(AdminLicenseTypesService);
    private readonly confirmationDialogService = inject(ConfirmationDialogService);
    private readonly notificationService = inject(NotificationService);

    protected readonly queryOptions = usePagedList<LicenseTypeQueryOptions>({
        initial: { sort: "asc", col: "name", page: 1, size: 10 },
        totalCount: () => this.adminLicenseTypesResource.value()?.totalCount ?? 0,
        pageKey: "page",
        pageSizeKey: "size"
    });

    protected adminLicenseTypesResource = this.adminLicenseTypesService.getAdminLicenseTypesResource(this.queryOptions.query);
    public adminLicenseTypes = computed(() => this.adminLicenseTypesResource.value());

    protected compatibleConnectorsTemplate = viewChild<TemplateRef<LicenseType>>("compatibleConnectorsTemplate");

    protected columns = computed((): TableColumn<LicenseType>[] => [
        {
            field: "name",
            headerText: "Name",
            canSort: true,
            width: "25%"
        },
        {
            field: "description",
            headerText: "Description",
            canSort: true,
            width: "25%"
        },
        {
            field: "category",
            headerText: "Category",
            canSort: true,
            width: "15%"
        },
        {
            field: "apps",
            headerText: "Compatible Connectors",
            width: "30%",
            templateRef: this.compatibleConnectorsTemplate()
        }
    ]);

    public async edit(adminLicenseType?: LicenseType): Promise<void> {
        const { result } = await this.dialogService.open(AdminLicenseTypesEditModalComponent, {
            inputValues: { adminLicenseType },
            headerText: adminLicenseType ? "Update License Type" : "Create License Type"
        });
        if (result) {
            const message = adminLicenseType ? "License type was saved." : "License type was created.";
            this.notificationService.success(message);
            this.adminLicenseTypesResource.reload();
        }
    }

    public async deleteAdminLicenseType(adminLicenseType: LicenseType): Promise<void> {
        const { result } = await this.confirmationDialogService.open({
            headerText: "Delete License Type",
            bodyText: `<p>You are about to delete <strong>${adminLicenseType.name}</strong></p><p>Are you sure?</p>`,
            confirmFn: async () => {
                await this.adminLicenseTypesService.deleteAdminLicenseType(adminLicenseType.id);
            },
            successNotificationText: "License type has been deleted.",
            errorNotificationText: "License type was not found."
        });

        if (result) {
            reloadAfterRemoval(this.queryOptions, this.adminLicenseTypes()?.items.length ?? 0, () => this.adminLicenseTypesResource.reload());
        }
    }

    public onPageSizeChange(event: { pageSize: number }): void {
        this.queryOptions.change({ size: event.pageSize });
    }

    public sortChange(sortEvent: TableSortEvent): void {
        this.queryOptions.change({ sort: sortEvent.sortDir, col: sortEvent.field });
    }
}
